// src/utils/estimateLineTax.js — taxpick4-54573069 (tax codes on every line, Stage S4: the estimate page)
//
// The estimate page's tax, when the company's Settings › Taxes switch is ON. Pure (no Firebase), so
// every rule here is tested without the app.
//
// THREE MODES, decided from the live settings/taxCodes doc (lineTax.taxCodesSwitchOn):
//   'off'   — Taxes off: EVERY real company today. The page runs exactly the code it ran before S4
//             (estimateEditor.estimateTaxContext / taxChoicesFor / applyTaxChoice, the S2 drop on save).
//             Nothing in this file is used for it. While the doc is loading or can't be read, the
//             legacy list decides, so a Taxes-off company never waits and never fails a save here.
//             (S8: a Taxes-ON company whose settings can't be read must then block saving.)
//   'qb'    — Taxes on + QuickBooks: today's QuickBooks picker (taxCodeId), plus "Set tax for all
//             lines", the customer exemption and the stored tax parts.
//   'named' — Taxes on, no QuickBooks: the company's Settings › Taxes codes (salesTaxCodeId + the kept
//             copy salesTaxSnapshot, lineTax.js), "Stop using" codes never offered to new lines.
// The totals and the stored fields are the SERVER's (functions/utils/serverTotals.engineTotals): the
// same stamping, the same engine input, the same exemption stamp — so the shadow checker agrees.
import {
    taxCodesSwitchOn, lineTaxStatus, lineTaxChoices, applyLineTax, newLineTaxFields, stampLineTax,
    exemptionsSwitchOn, customerExemptionOf, taxPartLabel, NO_TAX, STOPPED_NOTE,
    fieldTaxCheckOf, fieldTaxCheckCount, clearFieldTaxCheck, FIELD_TAX_CHECK_TEXT,
} from './lineTax';
import { computeDocumentTotals, makeTaxCodeResolver, namedTaxLookups } from './totalsEngine';
import { estimateTaxContext, taxChoicesFor, applyTaxChoice, lineTaxValue, EXEMPT_CHOICE } from './estimateEditor';
import { appendPickedEstimateLines } from './catalogPickLines';
import { canUseCatalog } from './catalogUi';
import { formatCurrency } from './formatters';
import { startingTaxCodes } from './taxCodes';

export const TAX_PICK_MARKER = 'taxpick4-54573069';
export const TAX_MODES = Object.freeze({ OFF: 'off', QB: 'qb', NAMED: 'named' });
export { FIELD_TAX_CHECK_TEXT, STOPPED_NOTE };
export const SET_ALL_DONE = 'All lines updated. Change any line on its own below.';
export const TAX_SETTINGS_LOADING = 'Loading your tax settings…';

/** The page's tax mode. taxCodesDoc: undefined while loading, null when missing (useTaxCodesDoc). */
export function pageTaxMode({ orgId, taxCodesDoc, qbConnected }) {
    if (!taxCodesSwitchOn(orgId, taxCodesDoc === undefined ? null : taxCodesDoc)) return TAX_MODES.OFF;
    return qbConnected === true ? TAX_MODES.QB : TAX_MODES.NAMED;
}

/**
 * Who gets a tax picker (decision 6): office roles (Owner, Admin, Manager, Office Staff — the catalog
 * list) and Sales Consultants. Field Technicians and Lead Hands never (they can't open the page either).
 */
export const canPickLineTax = (role) => canUseCatalog(role) || role === 'Sales Consultant';
/** Who sees "Added in the field — check tax", and whose save clears it: office roles only. */
export const isOfficeTaxRole = (role) => canUseCatalog(role);

/**
 * Everything the engine needs for an ON mode. qb: today's QuickBooks resolver (estimateTaxContext);
 * named: the Settings › Taxes lookups (as serverTotals.engineResolver). The customer's exemption only
 * when exemptions are on.
 */
export function onModeContext({ mode, orgId, companySettings, taxOptions, taxLoaded, taxCodesDoc, customer }) {
    const doc = taxCodesDoc || null;
    // As the server (serverTotals.taxContext): the saved codes, else the carry-over code from the company rate.
    const namedCodes = startingTaxCodes(doc, companySettings || {}) || [];
    const legacyRate = Number(companySettings?.taxRate) || 0;
    const resolveTaxCode = mode === TAX_MODES.NAMED
        ? makeTaxCodeResolver(namedTaxLookups({ namedCodes, legacyRate }))
        : estimateTaxContext({ companySettings, taxOptions, taxLoaded }).resolveTaxCode;
    const exemption = exemptionsSwitchOn(orgId, doc) ? customerExemptionOf(customer) : null;
    return { mode, namedCodes, legacyRate, resolveTaxCode, exemption };
}

const isLine = (l) => l !== null && typeof l === 'object' && !Array.isArray(l);
const withoutKeys = (l, keys) => { const o = { ...l }; keys.forEach((k) => delete o[k]); return o; };

/**
 * The lines as an ON-mode save stores them: named → every taxed line gets a code + kept copy
 * (lineTax.stampLineTax — an older line takes the default it was shown as); qb → as they are.
 */
export function stampedLines(lines, ctx) {
    const list = Array.isArray(lines) ? lines : [];
    return ctx.mode === TAX_MODES.NAMED ? list.map((l) => (isLine(l) ? stampLineTax(l, ctx.namedCodes) : l)) : list;
}

/**
 * The totals for an ON mode = serverTotals.engineTotals: stamped lines, taxSnapshot left out of the
 * engine's input (the page never writes one), the customer's exemption unless the document switched
 * it off. → { totals (the engine's answer), lines (to store), storedFields, exemptionApplied }
 */
export function onModeTotals(lines, ctx, doc) {
    const stamped = stampedLines(lines, ctx);
    const bare = stamped.map((l) => (isLine(l) ? withoutKeys(l, ['taxSnapshot']) : l));
    const ex = ctx.exemption || null;
    const off = doc && doc.taxExemptionOff ? doc.taxExemptionOff : undefined;
    const totals = ex
        ? computeDocumentTotals({ lineItems: bare, taxExemptionOff: off }, { resolveTaxCode: ctx.resolveTaxCode, customerExemption: ex })
        : computeDocumentTotals({ lineItems: bare }, { resolveTaxCode: ctx.resolveTaxCode });
    const applied = !!ex && !totals.notes.some((n) => n.code === 'exemptionSwitchedOff');
    const storedFields = applied
        ? { taxParts: totals.taxParts, totalsEngineVersion: totals.engineVersion, taxExemptionApplied: { parts: ex.parts, number: ex.number } }
        : { taxParts: totals.taxParts, totalsEngineVersion: totals.engineVersion };
    return { totals, lines: stamped, storedFields, exemptionApplied: applied };
}

/**
 * An ON-mode save's tax fields over the write: lines stamped (and an office role's save clears the
 * field markers), the stored parts / engine version / exemption stamp. An edit whose exemption no
 * longer applies deletes the old stamp (deleteValue). The write is changed in place and returned.
 */
export function applyOnModeSave(write, { result, ctx, isEdit, isOfficeRole, deleteValue }) {
    write.lineItems = clearFieldTaxCheck(stampedLines(write.lineItems, ctx), isOfficeRole === true);
    write.subtotal = result.totals.subtotal;
    write.taxAmount = result.totals.taxAmount;
    write.total = result.totals.total;
    Object.assign(write, result.storedFields);
    if (isEdit && !result.exemptionApplied) write.taxExemptionApplied = deleteValue;
    return write;
}

// ── the picker ──────────────────────────────────────────────────────────────

/**
 * One line's Tax dropdown in an ON mode → { value, choices: [{ value, label }], stopped, note, exempt }.
 * qb: today's QuickBooks choices. named: lineTax's choices; an older line shows the default; a line on a
 * stopped code shows it, marked, with the note.
 */
export function linePicker(line, ctx, taxCtx) {
    if (ctx.mode === TAX_MODES.QB) {
        const value = lineTaxValue(line);
        return { value, choices: taxChoicesFor(line, taxCtx), stopped: false, note: '', exempt: value === EXEMPT_CHOICE };
    }
    const status = lineTaxStatus(line, ctx.namedCodes);
    const choices = lineTaxChoices(ctx.namedCodes, line).map((c) => ({ value: c.value, label: c.label }));
    // No default code at all: an older line is charged the company rate — say so instead of a blank box.
    if (status.value === '') choices.unshift({ value: '', label: `Company ${ctx.legacyRate}%` });
    return { value: status.value, choices, stopped: status.stopped, note: status.note, exempt: status.value === NO_TAX };
}

/** A Tax dropdown choice applied to one line (ON modes). */
export function applyLinePick(line, value, ctx) {
    if (ctx.mode === TAX_MODES.QB) return applyTaxChoice(line, value);
    if (value === '') return line;
    return applyLineTax(line, value, ctx.namedCodes);
}

/** "Set tax for all lines": the choices a NEW line could have (no stopped codes) + No tax. */
export function setAllChoices(ctx, taxCtx) {
    if (ctx.mode === TAX_MODES.QB) return taxChoicesFor({}, taxCtx);
    return lineTaxChoices(ctx.namedCodes, null).map((c) => ({ value: c.value, label: c.label }));
}

/** Every line set to the chosen code (an ordinary edit; each line can still be changed on its own). */
export function setAllLines(lines, value, ctx) {
    return (Array.isArray(lines) ? lines : []).map((l) => (isLine(l) ? applyLinePick(l, value, ctx) : l));
}

// ── new lines from the catalog ──────────────────────────────────────────────

/** A new line takes the catalog item's own code (else the company default) — named mode only. */
export function withItemSalesTax(line, item, ctx) {
    if (ctx.mode !== TAX_MODES.NAMED) return line;
    const base = withoutKeys(line, ['salesTaxCodeId', 'salesTaxSnapshot']);
    return { ...base, ...newLineTaxFields(ctx.namedCodes, item) };
}

/** The catalog panel / quick add's "Add": today's append, then each new line takes its item's code. */
export function appendPicksWithTax(lines, picks, ids, ctx) {
    const out = appendPickedEstimateLines(lines, picks, ids);
    if (ctx.mode !== TAX_MODES.NAMED || !picks.length) return out;
    const first = out.length - picks.length;
    return out.map((l, i) => (i >= first ? withItemSalesTax(l, picks[i - first].item, ctx) : l));
}

// ── what the page says ──────────────────────────────────────────────────────

/** A totals row's label in an ON mode: "GST 5%", or "GST 5% — exempt #123" for an exempted part. */
export function onModeRowLabel(p) {
    const label = taxPartLabel(p);
    if (!p || !p.exempt) return label;
    const n = p.exempt && typeof p.exempt.number === 'string' ? p.exempt.number : '';
    return `${label} — exempt${n ? ` #${n}` : ''}`;
}

/**
 * Answer 1 (Marcus 2026-11-03): an older estimate re-totalled by a save. When the estimate's stored
 * lines, totalled the way a save now would, don't give the stored tax, saving changes its tax:
 * "Saving will change the tax on this estimate from $59.00 to $95.40." (newTax = the page's figure now).
 * null when nothing would change, for a new estimate, or when the stored tax isn't a number.
 */
export function taxChangeNote({ source, storedLinesTax, newTax, currency, docWordLower }) {
    if (!source || !source.id || typeof source.taxAmount !== 'number' || !isFinite(source.taxAmount)) return null;
    const cents = (v) => Math.round(Number(v) * 100);
    if (cents(storedLinesTax) === cents(source.taxAmount)) return null;
    if (cents(newTax) === cents(source.taxAmount)) return null;
    return `Saving will change the tax on this ${docWordLower || 'estimate'} from ${formatCurrency(source.taxAmount, currency)} to ${formatCurrency(newTax, currency)}.`;
}

export { fieldTaxCheckOf, fieldTaxCheckCount };
